import { useState } from 'react'

import {
  ResponsiveContainer,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ComposedChart,
  Line,
  TooltipContentProps,
  DotItemDotProps
} from 'recharts'
import { addDays, format, parse, subMonths } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { randomInt, range } from 'es-toolkit'
import useI18n from '@/shared/hooks/usei18n'
import {
  useEuaEmissionPriceForFixedTimeRange,
  useEuaEmissionPriceHistoryForExport,
  useEuaEmissionPriceOverviewByQuarter
} from '@/shared/hooks/euaEmissionPriceOverview'
import {
  getCurrentCalendarQuarter,
  getCurrentEuaPriceYear
} from '@/config/cbamReportingPeriods'
import { parseEuaPrice } from '@/shared/utils/parseEuaPrice'
import { downloadEuaPriceHistoryCsv } from '@/shared/utils/euaPriceHistoryCsv'
import toast from 'react-hot-toast'
import {
  ArtemSpinner,
  ButtonSize,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DataBlock,
  IconButton,
  ToggleButton
} from '@/common'
import { Tooltip as ArtemTooltip } from '@/common/components/Tooltip/Tooltip'
import { DownloadArrowDownIcon } from '@/common/components/icons'
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent'
import { ActiveDotProps } from 'recharts/types/util/types'

function CustomTooltip(props: TooltipContentProps<ValueType, NameType>) {
  const { currencyFormatterWith3Decimal } = useI18n()
  const { payload, label } = props
  if (payload) {
    return (
      <div className="rounded-xs shadow-chartTooltip bg-white border px-7 py-2 text-center">
        <p className="text-xs text-text-secondary mb-1">{label}</p>
        <p className="text-sm font-semibold">
          {currencyFormatterWith3Decimal(Number(payload[0]?.value) || 0)}
        </p>
      </div>
    )
  }
}

const RECHARTS_DATE_FORMAT = 'dd/MM/yyyy'

function CustomXAxisTick(props: {
  x: number | string
  y: number | string
  payload: { value: string; index: number }
  visibleTicksCount: number
}) {
  const {
    x,
    y,
    payload: { index, value }
  } = props

  const { shortDateFormatter } = useI18n()
  const parsedDate = parse(value, RECHARTS_DATE_FORMAT, new Date())
  const isFirst = index === 0
  const isLast = index === props.visibleTicksCount - 1
  if (!isFirst && !isLast) {
    return null
  }

  return (
    <text
      x={Number(x)}
      y={Number(y) + 20}
      className="text-gray-500 text-xs"
      fill="currentColor"
      textAnchor={index === 0 ? 'start' : 'end'}
    >
      {shortDateFormatter(parsedDate)}
    </text>
  )
}

function CustomYAxisTick(props: {
  x: number | string
  y: number | string
  payload: { value: string }
}) {
  const {
    x,
    y,
    payload: { value }
  } = props

  const { numberFormatter2Decimals } = useI18n()

  return (
    <text
      x={Number(x) - 20}
      y={Number(y) - 2}
      className="text-gray-500 text-xs"
      fill="currentColor"
      textAnchor="middle"
    >
      {numberFormatter2Decimals(Number(value))}
    </text>
  )
}

function CustomDot(props: DotItemDotProps | ActiveDotProps) {
  return (
    <circle
      cx={props.cx}
      cy={props.cy}
      r={6}
      stroke="#FFFFFF"
      strokeWidth={2}
      className="text-green-600"
      fill="currentColor"
    />
  )
}

enum FixedTimeRange {
  '1m' = '1m',
  '3m' = '3m',
  '6m' = '6m',
  '1y' = '1y',
  'max' = 'max'
}

function getQueryPAramsForFixedTimeRange(timeRange: FixedTimeRange): {
  startDate: Date
  endDate: Date
} {
  switch (timeRange) {
    case FixedTimeRange['1m']:
      return {
        startDate: subMonths(new Date(), 1),
        endDate: new Date()
      }

    case FixedTimeRange['3m']:
      return {
        startDate: subMonths(new Date(), 3),
        endDate: new Date()
      }
    case FixedTimeRange['6m']:
      return {
        startDate: subMonths(new Date(), 6),
        endDate: new Date()
      }
    case FixedTimeRange['1y']:
      return {
        startDate: subMonths(new Date(), 12),
        endDate: new Date()
      }
    case FixedTimeRange['max']:
      return {
        startDate: subMonths(new Date(), 60),
        endDate: new Date()
      }
    default:
      return {
        startDate: subMonths(new Date(), 60),
        endDate: subMonths(new Date(), 1)
      }
  }
}

export default function EuaEmissionPriceOverviewWidget({
  toggleButtonSize = 'sm',
  useFakeForecast = false
}: {
  useFakeForecast?: boolean
  toggleButtonSize?: ButtonSize
}) {
  const { t } = useTranslation()
  const { currencyFormatter2Decimals } = useI18n()
  const euaPriceYear = getCurrentEuaPriceYear()
  const currentQuarter = getCurrentCalendarQuarter()
  const [timeRange, setTimeRange] = useState<FixedTimeRange>(FixedTimeRange['1m'])
  const timeRangeQueryParams = getQueryPAramsForFixedTimeRange(timeRange)
  const { data: euaEmissionPriceOverview, isLoading } = useEuaEmissionPriceForFixedTimeRange(
    timeRangeQueryParams.startDate,
    timeRangeQueryParams.endDate
  )
  const { data: euaByQuarter, isLoading: isQuarterlyLoading } =
    useEuaEmissionPriceOverviewByQuarter(euaPriceYear ?? 2026, {
      enabled: euaPriceYear != null
    })
  const { refetch: refetchAllEuaPrices, isFetching: isDownloadingPrices } =
    useEuaEmissionPriceHistoryForExport()

  const handleDownloadPrices = async () => {
    try {
      const result = await refetchAllEuaPrices()
      if (result.error) {
        throw result.error
      }

      downloadEuaPriceHistoryCsv(result.data?.euaEmissionPriceHistory ?? [], {
        date: t('dashboard:euaEmissionPriceOverviewWidget.export.columns.date'),
        price: t('dashboard:euaEmissionPriceOverviewWidget.export.columns.price')
      })
    } catch {
      toast.error(t('dashboard:euaEmissionPriceOverviewWidget.export.error'), {
        position: 'top-right'
      })
    }
  }

  const lastEuaPrice = parseEuaPrice(euaEmissionPriceOverview?.lastEuaPrice)
  const quarterlyPriceRaw =
    euaByQuarter?.quarters.find(quarter => quarter.value === currentQuarter)?.averagePrice ??
    null
  const displayYear = euaByQuarter?.year ?? (euaPriceYear != null ? String(euaPriceYear) : '')
  const euaEmissionPriceHistory = euaEmissionPriceOverview?.euaEmissionPriceHistory || []

  const formattedLastEuaPrice = lastEuaPrice ? currencyFormatter2Decimals(lastEuaPrice) : null
  const formattedQuarterlyWeightedAveragePrice =
    quarterlyPriceRaw != null && quarterlyPriceRaw !== ''
      ? currencyFormatter2Decimals(parseEuaPrice(quarterlyPriceRaw))
      : '–'
  const isQuarterlyPriceLoading = euaPriceYear != null && isQuarterlyLoading

  const lastTradeDateRaw = euaEmissionPriceHistory[euaEmissionPriceHistory.length - 1]?.tradeDate
  const lastTradeDate = lastTradeDateRaw ? new Date(lastTradeDateRaw) : null
  const lastPrice = parseEuaPrice(
    euaEmissionPriceHistory[euaEmissionPriceHistory.length - 1]?.price
  )

  const forecast = lastTradeDate
    ? range(0, 60).map(index => ({
        tradeDate: addDays(lastTradeDate, index + 1),
        forecast: lastPrice + randomInt(0, index + 1),
        price: 0
      }))
    : []

  const formattedHistory = euaEmissionPriceHistory.map(euaPriceData => ({
    price: parseEuaPrice(euaPriceData.price),
    forecast: null,
    tradeDate: format(new Date(euaPriceData.tradeDate), 'dd/MM/yyyy')
  }))

  const formattedForecast = forecast.map(item => ({
    price: null,
    forecast: item.forecast,
    tradeDate: format(new Date(item.tradeDate), 'dd/MM/yyyy')
  }))

  const historyLength = formattedHistory ? formattedHistory.length : 0

  return (
    <Card>
      <CardHeader className="flex justify-between items-center">
        <CardTitle title={t('dashboard:euaEmissionPriceOverviewWidget.title')} />
        <div className="flex gap-2 items-center">
          {isDownloadingPrices ? (
            <div className="flex items-center justify-center size-9" aria-busy="true">
              <ArtemSpinner className="size-4" />
            </div>
          ) : (
            <ArtemTooltip
              content={t('dashboard:euaEmissionPriceOverviewWidget.actions.downloadPricesTooltip')}
            >
              <span className="inline-flex">
                <IconButton
                  variant="subtle"
                  sizeType={toggleButtonSize}
                  icon={<DownloadArrowDownIcon />}
                  onClick={handleDownloadPrices}
                  aria-label={t(
                    'dashboard:euaEmissionPriceOverviewWidget.actions.downloadPricesTooltip'
                  )}
                />
              </span>
            </ArtemTooltip>
          )}
          {Object.values(FixedTimeRange).map(timeRangeItem => (
            <ToggleButton
              key={timeRangeItem}
              onClick={() => setTimeRange(timeRangeItem)}
              isActive={timeRange === timeRangeItem}
              className="h-10 w-16"
              sizeType={toggleButtonSize}
            >
              {t(`dashboard:euaEmissionPriceOverviewWidget.timeRange.${timeRangeItem}`)}
            </ToggleButton>
          ))}
        </div>
      </CardHeader>
      <CardBody>
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 order-2 lg:order-0">
            <ResponsiveContainer
              className="bg-gray-300 border border-gray-400 pt-2"
              width="100%"
              height="100%"
            >
              <ComposedChart
                width={500}
                height={400}
                data={[...formattedHistory, ...(useFakeForecast ? formattedForecast : [])]}
                margin={{
                  top: 20,
                  right: 20,
                  bottom: 20,
                  left: 20
                }}
              >
                <Area
                  type="monotone"
                  className="text-green-600 fill-green-300"
                  stroke="currentColor"
                  strokeWidth={2}
                  dataKey="price"
                  fill="#e3fffa"
                  activeDot={props => <CustomDot {...props} />}
                  dot={props =>
                    props.index === historyLength - 1 ? (
                      <CustomDot key={props.index} {...props} />
                    ) : (
                      <></>
                    )
                  }
                />
                {useFakeForecast && (
                  <Line
                    connectNulls={false}
                    type="monotone"
                    strokeDasharray="2 2"
                    dataKey="forecast"
                    className="text-blue-500"
                    stroke="currentColor"
                    strokeWidth={2}
                    dot={false}
                  />
                )}
                <YAxis
                  type="number"
                  className="text-xs text-gray-500"
                  stroke="currentColor"
                  tickLine={false}
                  domain={['dataMin - 5', 'dataMax + 5']}
                  tick={props => <CustomYAxisTick {...props} />}
                />
                <XAxis
                  interval={0}
                  tickLine={false}
                  className="text-xs text-gray-500"
                  stroke="currentColor"
                  dataKey="tradeDate"
                  tick={props => <CustomXAxisTick {...props} />}
                />
                <Tooltip
                  content={(props: TooltipContentProps<ValueType, NameType>) =>
                    props.active ? <CustomTooltip {...props} /> : <></>
                  }
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="order-1 lg:order-0">
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
              <DataBlock
                label={t('dashboard:euaEmissionPriceOverviewWidget.latestEuaPrice')}
                data={
                  isLoading ? (
                    <ArtemSpinner className="size-4 inline-block" />
                  ) : (
                    formattedLastEuaPrice
                  )
                }
              />
              <DataBlock
                label={t('dashboard:euaEmissionPriceOverviewWidget.quarterlyWeightedAverage', {
                  quarter: currentQuarter,
                  year: displayYear
                })}
                data={
                  isQuarterlyPriceLoading ? (
                    <ArtemSpinner className="size-4 inline-block" />
                  ) : (
                    formattedQuarterlyWeightedAveragePrice
                  )
                }
              />
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
